import axios from 'axios';

// Determine API base URL based on current environment
// In production (served via nginx on domain), use relative path
// In development (localhost), use explicit localhost:37483
const getApiBaseUrl = () => {
  const { hostname, protocol } = window.location;
  
  // Check if we're in development (localhost or 127.0.0.1)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:37483/api';
  }
  
  // For production, use relative URL (same origin)
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Device API functions
export const fetchDevices = async () => {
  try {
    const response = await api.get('/devices');
    return response.data.devices;
  } catch (error) {
    console.error('Error fetching devices:', error);
    // Fallback to hardcoded devices if API fails
    return getFallbackDevices();
  }
};

export const fetchDatasheet = async () => {
  try {
    const response = await api.get('/datasheet');
    return response.data;
  } catch (error) {
    console.error('Error fetching datasheet:', error);
    // Fallback to hardcoded datasheet
    return getFallbackDatasheet();
  }
};

export const calculateMass = async (devices) => {
  try {
    const requestData = {
      items: devices.map(device => ({
        device_type: device.deviceType,
        quantity: device.quantity
      }))
    };

    const response = await api.post('/calculate', requestData);
    return response.data;
  } catch (error) {
    console.error('Error calculating mass:', error);
    
    // Fallback to client-side calculation if API fails
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    
    // Try client-side calculation as fallback
    return calculateMassClientSide(devices);
  }
};

// Client-side fallback calculation
const calculateMassClientSide = (devices) => {
  const datasheet = getFallbackDatasheet();
  const results = [];
  let totalMass = 0;

  devices.forEach(device => {
    const deviceData = datasheet[device.deviceType];
    if (!deviceData) {
      throw new Error(`Unknown device type: ${device.deviceType}`);
    }

    const boxQuantity = Math.ceil(device.quantity / deviceData.in_box_num);
    const deviceMass = deviceData.mass * device.quantity;
    const boxMass = deviceData.box_mass * boxQuantity;
    const totalDeviceMass = deviceMass + boxMass;
    const boxSizeStr = deviceData.box_size.join('x');

    results.push({
      device_type: device.deviceType,
      mass: totalDeviceMass,
      box_quantity: boxQuantity,
      box_size: boxSizeStr,
      total_mass: totalDeviceMass
    });

    totalMass += totalDeviceMass;
  });

  return {
    results,
    total_mass: totalMass
  };
};

// Fallback data (same as backend)
const getFallbackDevices = () => {
  return [
    "TD BLE 1000 4шт",
    "TD BLE 1000 8шт",
    "TD BLE 1500",
    "TD BLE 2000",
    "TD BLE 2500",
    "TD BLE 3000",
    "Wired 1000 3шт",
    "Wired 1000 6шт",
    "Wired 1500",
    "Wired 2000",
    "Wired 2500",
    "Wired 3000",
    "TD BLE L без бат",
    "TD 150 BLE L 202",
    "TD 150 BLE L 262",
    "TD 150 BLE L 1000 без жгута",
    "TD 150 BLE L 500",
    "TD 150 BLE L 750"
  ];
};

const getFallbackDatasheet = () => {
  return {
    "TD BLE 1000 4шт": {
      "in_box_num": 4,
      "mass": 0.6,
      "box_mass": 0.4,
      "box_size": [109, 15, 13]
    },
    "TD BLE 1000 8шт": {
      "in_box_num": 8,
      "mass": 0.6,
      "box_mass": 0.6,
      "box_size": [119, 26, 14]
    },
    "TD BLE 1500": {
      "in_box_num": 8,
      "mass": 0.75,
      "box_mass": 9.5,
      "box_size": [168, 28, 13]
    },
    "TD BLE 2000": {
      "in_box_num": 8,
      "mass": 0.9,
      "box_mass": 12,
      "box_size": [218, 28, 13]
    },
    "TD BLE 2500": {
      "in_box_num": 8,
      "mass": 1.65,
      "box_mass": 15,
      "box_size": [268, 28, 13]
    },
    "TD BLE 3000": {
      "in_box_num": 8,
      "mass": 1.8,
      "box_mass": 18.5,
      "box_size": [318, 28, 13]
    },
    "Wired 1000 3шт": {
      "in_box_num": 3,
      "mass": 1,
      "box_mass": 0.4,
      "box_size": [109, 15, 13]
    },
    "Wired 1000 6шт": {
      "in_box_num": 6,
      "mass": 1,
      "box_mass": 0.6,
      "box_size": [199, 26, 14]
    },
    "Wired 1500": {
      "in_box_num": 6,
      "mass": 1.25,
      "box_mass": 9.5,
      "box_size": [168, 28, 13]
    },
    "Wired 2000": {
      "in_box_num": 6,
      "mass": 1.4,
      "box_mass": 12,
      "box_size": [218, 28, 13]
    },
    "Wired 2500": {
      "in_box_num": 6,
      "mass": 1.55,
      "box_mass": 15,
      "box_size": [268, 28, 13]
    },
    "Wired 3000": {
      "in_box_num": 6,
      "mass": 1.7,
      "box_mass": 18.5,
      "box_size": [318, 28, 13]
    },
    "TD BLE L без бат": {
      "in_box_num": 8,
      "mass": 0.55,
      "box_mass": 0.6,
      "box_size": [119, 26, 14]
    },
    "TD 150 BLE L 202": {
      "in_box_num": 20,
      "mass": 0.335,
      "box_mass": 0.6,
      "box_size": [119, 26, 14]
    },
    "TD 150 BLE L 262": {
      "in_box_num": 20,
      "mass": 0.355,
      "box_mass": 0.6,
      "box_size": [119, 26, 14]
    },
    "TD 150 BLE L 1000 без жгута": {
      "in_box_num": 8,
      "mass": 0.575,
      "box_mass": 0.6,
      "box_size": [119, 26, 14]
    },
    "TD 150 BLE L 500": {
      "in_box_num": 16,
      "mass": 0.412,
      "box_mass": 0.6,
      "box_size": [119, 26, 14]
    },
    "TD 150 BLE L 750": {
      "in_box_num": 10,
      "mass": 0.480,
      "box_mass": 0.6,
      "box_size": [119, 26, 14]
    }
  };
};

export default api;